import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute } from '@angular/router';
import { OutputResponseDTO, TestOutputDTO } from 'src/app/model/dto/test-output/get-test-output-dto';
import { ContractsService } from 'src/app/services/contracts.service';
import { SnackService } from 'src/app/services/snack.service';
import { GeneralItemDetailDialogComponent } from 'src/app/shared-components/general-item-detail-dialog/general-item-detail-dialog.component';
import { TestOutputInfoDialogComponent } from '../test-output-info-dialog/test-output-info-dialog.component';
import { BoxesService } from 'src/app/services/boxes-service';
import { Box } from 'src/app/model/box';
import { BoxInterfaceInfoDialogComponent } from '../../boxes/box-interface-info-dialog/box-interface-info-dialog.component';
import gravatarUrl from 'gravatar-url';
import { AuthService } from 'src/app/services/auth.service';
import { PdfReportService } from 'src/app/services/pdf-report.service';

const refreshTimeout = 30 * 1000; // 30 seconds

@Component({
  selector: 'aw-test-output-page',
  templateUrl: './test-output-page.component.html',
  styleUrls: ['./test-output-page.component.scss']
})
export class TestOutputPageComponent implements OnInit, OnDestroy {

  displayedColumns: string[] = ['Report', 'User', 'Testname', 'Box', 'Interface', 'Created at', 'Completed at', 'Execution Status', 'Result', 'output'];
  dataSource = null;
  contractId: number;
  loading = true;
  cachedOutputs: any = {};
  isForbidden: boolean = false;

  loggedUser = null;

  box_status= [];

  timer: any;

  selectedForReport: Set<string> = new Set();

  /** Paginator vars */
  length = 0;
  pageSize = 50;
  pageIndexRequest = 1;
  disablePaginator = false;
  errorMessage: string = '';



  constructor(public dialog: MatDialog, private snackbar: SnackService, private route: ActivatedRoute, private contractService: ContractsService, private boxService: BoxesService, private pdfReportService: PdfReportService, public authService: AuthService) { }

  ngOnInit(): void {

    this.loggedUser = this.authService.loggedUser();

    this.route.params.subscribe(params => {
      this.contractId = params['id'];
    });

    this.contractService.getContract(this.contractId).subscribe(response=> {
      this.boxService.getAllBoxesByContract(response).subscribe(response => {
        this.box_status=response;
    });
    },
      error => {
        if (error.message === "aw.api.errors.forbidden") {
          this.isForbidden = true;
          this.errorMessage = 'The user is not authorized to fetch the test list of this contract.';
        }
      });

    this.contractService.getContractsOutputList(this.contractId, this.pageIndexRequest).subscribe(response => {
      this.dataSource = response as OutputResponseDTO;
      this.length = response.count;
      if (this.length < 50)
        this.disablePaginator = true;
      this.loading = false;
    },
      error => {
        this.loading = false;
        if (error.message === "aw.api.errors.forbidden") {
          this.errorMessage = 'The user is not authorized to fetch the test list of this contract.';
        }
      });

    // create an interval to update the list every 10 seconds
    this.timer = setInterval(() => {
      this.contractService.getContractsOutputList(this.contractId, this.pageIndexRequest).subscribe(response => {
        this.dataSource = response as OutputResponseDTO;
      });
    }, refreshTimeout);
  }
  
  ngOnDestroy() {
    clearInterval(this.timer);
 }

  viewOutput(exec: any) {
    
    let execId = exec.id_run;
    if (this.cachedOutputs[execId]) {
      this.details(exec, this.cachedOutputs[execId]);
      return;
    }

    this.contractService.getContractsOutputTest(this.contractId, execId).subscribe(response => {
      let detailedTest = response as any;
      const list_urls = detailedTest.output_urls;
      let results = new Map<number, any>();

      let requests = list_urls.map((url, index) => {
        return this.contractService.getGenericOutput(url).toPromise().then(response => {
          results.set(index, 
            {
              url: index,
              response: response
            });
        });
      });

      Promise.all(requests).then(() => {        
        // Convert Map to list (array of values) and order it by url field
        let resultsList = Array.from(results.values()).sort((a, b) => b.url - a.url).map((item) => item.response);
        
        this.cachedOutputs[execId] = resultsList;
        this.details(exec, resultsList);
      });
    });
  }

  // private details(execOutput: any): void {
  //   this.dialog.open(GeneralItemDetailDialogComponent, { data: execOutput });
  // }
  

  details(dataTable: any, dataOutput: any) {
    this.dialog.open(TestOutputInfoDialogComponent, {
      data: {
        dataTable: dataTable,
        dataOutput: dataOutput
      }
    });
  }

  downloadOutput(exec_id: string) {
    this.contractService.getContractsOutputTest(this.contractId, exec_id + '?download=true').subscribe(response => {
      let res = response as any;
      window.open(res.output, "_blank");
    });
  }
  
reportOutput(exec_id: string): Promise<{testData: any, testName: string}> {
  return new Promise((resolve, reject) => {
    
    this.contractService.getContractsOutputTest(this.contractId, exec_id + '?report=true').subscribe(
      response => {
        let testMetadata = response as any;
        
        const reportUrl = testMetadata.report;
        const testName = testMetadata.test_name;
        
        
        if (!reportUrl) {
          console.error('No report URL found in metadata');
          reject(new Error('No report URL found'));
          return;
        }
        
        this.contractService.getGenericOutput(reportUrl).subscribe(
  reportData => {
    if (!reportData) {
      console.error('Report data is empty or invalid from', reportUrl);
      reject(new Error('Invalid report data'));
      return;
    }
    resolve({ testData: reportData, testName: testName });
    },
    error => {
      console.error('Error fetching report data:', error);
      reject(error);
    }
    );
      },
      error => {
        console.error('Error fetching metadata:', error);
        reject(error);
      }
    );
  });
}

reportOutputs(exec_ids: string[]): Promise<{ testData: any, reportUrl: string }[]> {
  return new Promise((resolve, reject) => {
    this.contractService.getUrlMultipleReports(this.contractId, exec_ids).subscribe(
      (response: any) => {
        if (!response || !Array.isArray(response.reports_urls)) {
          console.error('Invalid response format: missing reports_urls');
          reject(new Error('Invalid response format'));
          return;
        }

        const reportPromises = response.reports_urls.map((reportUrl: string) => {
          return new Promise<{ testData: any; reportUrl: string }>((res, rej) => {
            this.contractService.getGenericOutput(reportUrl).subscribe(
              reportData => {
                if (!reportData) {
                  console.error('Report data is empty or invalid from', reportUrl);
                  rej(new Error('Invalid report data'));
                  return;
                }
                res({ testData: reportData, reportUrl });
              },
              error => {
                console.error('Error fetching report data:', error);
                rej(error);
              }
            );
          });
        });

        Promise.all(reportPromises)
          .then(results => resolve(results))
          .catch(err => reject(err));
      },
      error => {
        console.error('Error fetching report URLs:', error);
        reject(error);
      }
    );
  });
}


  handlePageEvent(e: PageEvent) {
    if (e.pageIndex > e.previousPageIndex) {
      this.pageIndexRequest += 1;
    }
    else {
      this.pageIndexRequest -= 1;
    }
    this.loading = true;
    this.contractService.getContractsOutputList(this.contractId, this.pageIndexRequest).subscribe(response => {
      this.dataSource = response as OutputResponseDTO;
      this.loading = false;
    });
  }

  deleteTestInExecution(exec_id: string) {
    this.snackbar.info('Removing test in execution...');

    this.contractService.deleteTestInExecution(this.contractId, exec_id).subscribe(response => {
      this.snackbar.success('Removed test in execution');

      // refresh output list
      this.contractService.getContractsOutputList(this.contractId, this.pageIndexRequest).subscribe(response => {
        this.dataSource = response as OutputResponseDTO;
      });

    }, error => {
      this.snackbar.info('Unable to remove test in execution');
    })
  }

  showBoxInfo(box: Box) {
    this.dialog.open(BoxInterfaceInfoDialogComponent, {
      data : {
        contract: this.contractId,
        box: box
      }
    })
  }

  getProfileImage(email: string) {

    const options = {
      rating: 'g',
      size: 32
    }

    return gravatarUrl(email) + `?d=retro&r=${options.rating}&s=${options.size}`;


  }
  onReportChange(element: any, isChecked: boolean) {
    if (isChecked) {
      this.selectedForReport.add(element.id_run);
    } else {
      this.selectedForReport.delete(element.id_run);
    }
  }

  generateConsolidatedReport() {
    if (this.selectedForReport.size === 0) {
    this.snackbar.info('Please select at least one test for report generation');
    return;
  }

  const selectedIds = Array.from(this.selectedForReport);
  this.snackbar.info('Fetching test data for report generation...'); 
  if (this.selectedForReport.size === 1) {
    const execId = selectedIds[0];
    
  this.reportOutput(execId).then(result => {
  if (result.testData && !Array.isArray(result.testData)) {
    result.testData=fillEmptySections(result.testData);
  }
  this.pdfReportService.downloadPdf(
    result.testData,
    `${result.testName}-report.pdf`,
    `${result.testName} Report`
  );
    
    this.selectedForReport.clear();
  })
  .catch(error => {
    console.error('Error generating single report:', error);
    this.snackbar.dismiss();
    this.snackbar.error('Failed to generate report');
  });
    
  } else {

    // const reportPromises = ;
    
    this.reportOutputs([...this.selectedForReport]).then(results => {
      const testsData = results.map(result => fillEmptySections(result.testData));
      const titles = results.map(result => result.testData.title);
      this.pdfReportService.downloadMergedPdf(
        testsData, 
        'diagnostic-suite-report.pdf', 
        titles, 
        'Diagnostic Test Suite Report'
      );
      this.selectedForReport.clear();
    })
    .catch(error => {
      console.error('Error generating multiple reports:', error);
      this.snackbar.error('Failed to generate reports');
    });

  }
  
  }

 
  isSelectedForReport(id_run: string): boolean {
    return this.selectedForReport.has(id_run);
  }
  hasAnySelectedForReport(): boolean {
  return this.selectedForReport.size > 4;
}
}
function fillEmptySections(obj: any): any {
  if (obj === null || obj === undefined) {
    return { "": "" };
  }

  if (Array.isArray(obj)) {
    if (obj.length === 0) {
      return [{ "": "" }];
    }
    return obj.map(fillEmptySections);
  }

  if (typeof obj === 'object') {
    const keys = Object.keys(obj);
    if (keys.length === 0) {
      return { "": "" };
    }
    const newObj: Record<string, any> = {};
    for (const key of keys) {
      newObj[key] = fillEmptySections(obj[key]);
    }
    return newObj;
  }
  return obj;
}


